import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from './components/Navbar';
import HeroSection from './components/HeroSection';
import TestimonialsSection from './components/TestimonialsSection';
import CTASection from './components/CTASection';
import Footer from './components/Footer';
import WhatsAppButton from './components/WhatsAppButton';
import AppointmentModal from './components/AppointmentModal';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const pages = [
  {
    to: '/nosotros',
    icon: 'ri-team-line',
    label: 'Nosotros',
    desc: 'Conoce a nuestro equipo de especialistas certificados y nuestra historia.',
    color: 'from-[#3A86FF]/15 to-[#0A2540]/10',
    iconColor: 'text-[#3A86FF]',
    img: 'https://readdy.ai/api/search-image?query=professional%20dental%20team%20doctors%20smiling%20white%20coats%20modern%20clinic%20bright&width=400&height=220&seq=ov-nosotros-001&orientation=landscape',
  },
  {
    to: '/servicios',
    icon: 'ri-hospital-line',
    label: 'Servicios',
    desc: 'Rehabilitación, estética, ortodoncia, implantes y mucho más.',
    color: 'from-emerald-500/15 to-emerald-900/10',
    iconColor: 'text-emerald-500',
    img: 'https://readdy.ai/api/search-image?query=modern%20dental%20equipment%20professional%20instruments%20tools%20clean%20dental%20chair%20clinic&width=400&height=220&seq=ov-servicios-001&orientation=landscape',
  },
  {
    to: '/opiniones',
    icon: 'ri-star-line',
    label: 'Opiniones',
    desc: '15 reseñas verificadas con calificación perfecta de 5 estrellas.',
    color: 'from-amber-500/15 to-amber-900/10',
    iconColor: 'text-amber-500',
    img: 'https://readdy.ai/api/search-image?query=happy%20smiling%20patient%20dental%20clinic%20satisfied%20treatment%20result%20bright%20clean&width=400&height=220&seq=ov-opiniones-001&orientation=landscape',
  },
  {
    to: '/sucursales',
    icon: 'ri-map-pin-2-line',
    label: 'Sucursales',
    desc: 'Encuéntranos en San Juan de Aragón, Gustavo A. Madero, CDMX.',
    color: 'from-violet-500/15 to-violet-900/10',
    iconColor: 'text-violet-500',
    img: 'https://readdy.ai/api/search-image?query=Mexico%20City%20CDMX%20street%20neighborhood%20map%20aerial%20view%20buildings%20daytime&width=400&height=220&seq=ov-sucursales-001&orientation=landscape',
  },
  {
    to: '/galeria',
    icon: 'ri-camera-smile-line',
    label: 'Galería',
    desc: 'Casos reales de antes y después. Transformaciones que hablan por sí solas.',
    color: 'from-rose-500/15 to-rose-900/10',
    iconColor: 'text-rose-500',
    img: 'https://readdy.ai/api/search-image?query=dental%20before%20after%20smile%20transformation%20result%20beautiful%20teeth%20gallery%20cases&width=400&height=220&seq=ov-galeria-001&orientation=landscape',
  },
  {
    to: '/contacto',
    icon: 'ri-mail-send-line',
    label: 'Contacto',
    desc: 'Escríbenos o agenda tu cita directamente. Respondemos en 24 h.',
    color: 'from-teal-500/15 to-teal-900/10',
    iconColor: 'text-teal-500',
    img: 'https://readdy.ai/api/search-image?query=dental%20clinic%20reception%20welcoming%20front%20desk%20bright%20modern%20professional%20blue%20white&width=400&height=220&seq=ov-contacto-001&orientation=landscape',
  },
];

function PagesOverviewSection() {
  return (
    <section className="py-28 bg-[#f7f9fc]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16 scroll-animate">
          <span className="inline-block text-[#3A86FF] font-semibold text-sm tracking-widest uppercase mb-3">
            Explora el sitio
          </span>
          <h2 className="text-4xl md:text-5xl font-extrabold text-[#0A2540] mb-5">
            Todo lo que{' '}
            <span className="bg-gradient-to-r from-[#3A86FF] to-[#0A2540] bg-clip-text text-transparent">
              necesitas saber
            </span>
          </h2>
          <p className="text-gray-500 text-lg max-w-xl mx-auto font-light">
            Navega cada sección de forma independiente para encontrar exactamente lo que buscas.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7">
          {pages.map((page, i) => (
            <Link
              key={page.to}
              to={page.to}
              className={`group bg-white rounded-3xl overflow-hidden border border-gray-100 hover:-translate-y-2 transition-all duration-300 scroll-animate anim-delay-${i * 100} flex flex-col cursor-pointer`}
            >
              <div className="relative overflow-hidden" style={{ height: '180px' }}>
                <img
                  src={page.img}
                  alt={page.label}
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540]/60 to-transparent" />
                <div className="absolute bottom-4 left-4 flex items-center gap-2">
                  <div className={`w-8 h-8 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-lg`}>
                    <i className={`${page.icon} text-white text-sm`} />
                  </div>
                  <span className="text-white font-bold text-sm">{page.label}</span>
                </div>
              </div>
              <div className="p-5 flex flex-col flex-1">
                <p className="text-gray-500 text-sm leading-relaxed flex-1">{page.desc}</p>
                <div className="flex items-center gap-1 mt-4 text-[#3A86FF] text-sm font-semibold">
                  <span>Ver más</span>
                  <div className="w-4 h-4 flex items-center justify-center group-hover:translate-x-1 transition-transform">
                    <i className="ri-arrow-right-line text-sm" />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const [modalOpen, setModalOpen] = useState(false);
  useScrollAnimation();

  return (
    <div className="min-h-screen bg-white font-sans">
      <Navbar onOpenModal={() => setModalOpen(true)} />
      <main>
        <HeroSection onOpenModal={() => setModalOpen(true)} />
        <PagesOverviewSection />
        <TestimonialsSection />
        <CTASection onOpenModal={() => setModalOpen(true)} />
      </main>
      <Footer />
      <WhatsAppButton />
      <AppointmentModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  );
}
